import React, { useState } from 'react'
import { fetchWithCsrf } from '../utils/csrf'
import Modal from './Modal'

interface Book {
  id: number
  title: string
  author: string
  cover_url?: string
  description?: string
}

interface BookOption {
  id: number
  book: Book
  display_order: number
}

interface EarlyFinalizeProposalProps {
  proposalId: number
  options: BookOption[]
  deadlinePassed?: boolean
  onClose: () => void
  onSuccess: () => void
}

export default function EarlyFinalizeProposal({ proposalId, options, deadlinePassed, onClose, onSuccess }: EarlyFinalizeProposalProps) {
  const [selectedOptionId, setSelectedOptionId] = useState<number | null>(null)
  const [readingDeadline, setReadingDeadline] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async () => {
    if (!selectedOptionId) {
      setError('Please select a book')
      return
    }

    if (!readingDeadline) {
      setError('Please set a reading deadline')
      return
    }

    setSubmitting(true)
    setError('')

    try {
      const endpoint = deadlinePassed ? 'finalize' : 'early_finalize'
      const response = await fetchWithCsrf(`/api/v1/book_proposals/${proposalId}/${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          selected_option_id: selectedOptionId,
          reading_deadline: readingDeadline
        })
      })

      if (response.ok) {
        onSuccess()
        onClose()
      } else {
        const data = await response.json()
        setError(data.error || 'Failed to finalize proposal')
      }
    } catch (err) {
      setError('Failed to finalize proposal')
    } finally {
      setSubmitting(false)
    }
  }

  // Get tomorrow's date as minimum
  const tomorrow = new Date()
  tomorrow.setDate(tomorrow.getDate() + 1)
  const minDate = tomorrow.toISOString().split('T')[0]

  const selectedOption = options.find(opt => opt.id === selectedOptionId)

  return (
    <Modal isOpen={true} onClose={onClose} size="xl" showCloseButton={true} className="overflow-hidden flex flex-col">
      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex justify-between items-start">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Select Winning Book</h2>
            <p className="text-gray-600 text-sm mt-1">Choose the book and end voting early</p>
          </div>
        </div>
      </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              Select the Winning Book
            </label>
            <div className="space-y-3">
              {options.map((option) => (
                <div
                  key={option.id}
                  onClick={() => setSelectedOptionId(option.id)}
                  className={`flex gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    selectedOptionId === option.id
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-200 hover:border-gray-300 bg-white'
                  }`}
                >
                  {/* Radio button */}
                  <div className="flex items-center">
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                      selectedOptionId === option.id
                        ? 'border-green-500 bg-green-500'
                        : 'border-gray-300'
                    }`}>
                      {selectedOptionId === option.id && (
                        <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                  </div>

                  {/* Book Info */}
                  <div className="flex gap-4 flex-1">
                    {option.book.cover_url ? (
                      <img
                        src={option.book.cover_url}
                        alt={option.book.title}
                        className="w-16 h-24 object-cover rounded shadow-sm flex-shrink-0"
                      />
                    ) : (
                      <div className="w-16 h-24 bg-gradient-to-br from-indigo-100 to-purple-100 rounded flex items-center justify-center flex-shrink-0">
                        <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                        </svg>
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900">{option.book.title}</h3>
                      <p className="text-sm text-gray-600">by {option.book.author}</p>
                      {option.book.description && (
                        <p className="text-sm text-gray-500 mt-1 line-clamp-2">{option.book.description}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {selectedOption && (
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Reading Deadline
              </label>
              <input
                type="date"
                value={readingDeadline}
                onChange={(e) => setReadingDeadline(e.target.value)}
                min={minDate}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-2">
                When should the club finish reading "{selectedOption.book.title}"?
              </p>
            </div>
          )}

          <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
            <div className="flex gap-3">
              <svg className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div className="text-sm text-amber-900">
                <p className="font-semibold mb-1">Warning:</p>
                <p className="text-amber-800">
                  This will immediately end the voting period and set the selected book as the winner. This action cannot be undone.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-6 py-3 bg-white text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-all border-2 border-gray-300"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting || !selectedOptionId || !readingDeadline}
              className="flex-1 bg-gradient-to-r from-green-500 to-emerald-500 text-white py-3 px-6 rounded-lg font-semibold hover:from-green-600 hover:to-emerald-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-green-500/30"
            >
              {submitting ? 'Finalizing...' : 'Select Winner & End Voting'}
            </button>
          </div>
        </div>
      </Modal>
  )
}
